import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, MapPin, Building2 } from "lucide-react";
import api from "@/api/client";

/** Modal that lets an operator search counties + places by fuzzy name, ranked
 * by score, and confirm a manual link in one click.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   contact: { email, jurisdiction_label?, state_abbr? }   – the contact being linked
 *   onResolved: (picked) => void                            – fired after the API confirms
 */
export default function JurisdictionPicker({ open, onClose, contact, onResolved }) {
  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Seed query + state from the contact's hints when the modal opens
  useEffect(() => {
    if (!open) return;
    setQ(contact?.jurisdiction_label?.replace(/,?\s*[A-Z]{2}$/, "").replace(/\s+County$/i, "") || "");
    setStateFilter(contact?.state_abbr || "");
    setResults([]);
  }, [open, contact]);

  const search = useCallback(async () => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ahj/jurisdictions/search", {
        params: { q, state: stateFilter || undefined, limit: 25 },
      });
      setResults(data.results || []);
    } catch (_e) { setResults([]); }
    finally { setLoading(false); }
  }, [q, stateFilter]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(search, 220);
    return () => clearTimeout(t);
  }, [open, search]);

  const handlePick = async (r) => {
    if (!contact?.email) return;
    setSubmitting(true);
    try {
      await api.post("/admin/ahj/backfill/manual-resolve", null, {
        params: {
          email: contact.email, scope: r.scope, key: r.key, label: r.label,
          state_abbr: r.state_abbr,
        },
      });
      onResolved?.(r);
      onClose?.();
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="jurisdiction-picker-modal">
        <DialogHeader>
          <DialogTitle className="font-outfit text-lg">Link <span className="font-mono text-sm">{contact?.email}</span></DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Search counties + places ranked by match score. One click confirms.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              placeholder="Type a jurisdiction (e.g. Phoenix, Maricopa, Mecklenburg)…"
              value={q} onChange={(e) => setQ(e.target.value)}
              className="pl-8 h-9 text-sm"
              autoFocus
              data-testid="picker-search-input"
            />
          </div>
          <Input
            placeholder="ST"
            value={stateFilter} onChange={(e) => setStateFilter(e.target.value.toUpperCase().slice(0, 2))}
            className="w-16 h-9 text-sm font-mono text-center"
            data-testid="picker-state-input"
          />
        </div>

        <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-md" data-testid="picker-results">
          {loading ? (
            <div className="text-xs text-slate-500 py-6 text-center">Searching…</div>
          ) : results.length === 0 ? (
            <div className="text-xs text-slate-500 py-6 text-center">
              {q.length < 2 ? "Type at least 2 characters to search" : "No matches"}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {results.map((r) => (
                <li key={`${r.scope}-${r.key}`}>
                  <button
                    type="button"
                    onClick={() => handlePick(r)}
                    disabled={submitting}
                    className="w-full px-3 py-2.5 text-left hover:bg-emerald-50 transition flex items-center gap-3 group disabled:opacity-50"
                    data-testid={`picker-result-${r.scope}-${r.key}`}
                  >
                    {r.scope === "place" ? (
                      <Building2 className="w-3.5 h-3.5 text-emerald-600 flex-none" />
                    ) : (
                      <MapPin className="w-3.5 h-3.5 text-indigo-600 flex-none" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{r.label}</div>
                      <div className="text-[10px] text-slate-500 font-mono flex items-center gap-2">
                        <span>{r.scope}</span>
                        <span>·</span>
                        <span>{r.key}</span>
                        {r.type && r.type !== "county" && <><span>·</span><span>{r.type}</span></>}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-[10px] font-mono">{r.score}</Badge>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end mt-3">
          <Button variant="ghost" onClick={onClose} data-testid="picker-cancel">Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
